import * as React from 'react'
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  Link,
  SvgIcon
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import routes from '../../routes'
import useStyles from './styles'
import BrandLogo from './BrandLogo'
import sidebarIcons from './sidebarIcons'
import { useLocation } from 'react-router-dom'
import { useTranslation, withTranslation } from 'react-i18next'

const drawerWidth = 260
function AdminSidebar(props) {
  const { t } = useTranslation()
  const location = useLocation()
  const classes = useStyles()
  const { window } = props
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const user = localStorage.getItem('user-borku')
    ? JSON.parse(localStorage.getItem('user-borku'))
    : null
  const userType = user?.userType
  const permissions = user?.permissions || []

  const isRouteAllowed = route => {
    if (!route.appearInSidebar || !route.admin) return false
    if (userType === 'ADMIN') return true
    if (userType === 'STAFF') {
      return permissions.includes('Admin') || permissions.includes(route.name)
    }
    return false
  }

  const generalRoutes = new Set([
    'Vendors',
    'Restaurants',
    'Orders',
    'Restaurant Sections',
    'Users',
    'Riders',
    'Staff',
    'Customer Support'
  ])
  const managementRoutes = new Set([
    'Configuration',
    'Coupons',
    'Cuisines',
    'Shop Types',
    'Restaurant Ads',
    'Tipping',
    'Zone',
    'Dispatch',
    'Courier',
    'Notifications',
    'Commission Rates',
    'Withdraw Requests',
    'Loyalty',
    'Referral'
  ])

  const allowedRoutes = routes.filter(isRouteAllowed)

  // Keep the active link visible instead of the list jumping back to the top
  React.useEffect(() => {
    document
      .querySelectorAll('[data-sidebar-active="true"]')
      .forEach(el => el.scrollIntoView({ block: 'nearest' }))
  }, [location.pathname])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  let lastSection = null
  const createLinks = (
    <Box className={classes.sidebarBox}>
      <Toolbar className={[classes.rowDisplay, classes.logo]}>
        <BrandLogo />
      </Toolbar>
      <Divider className={classes.sidebarDivider} />
      <Box className={classes.sidebarList}>
        {allowedRoutes.map((prop, key) => {
          const section = generalRoutes.has(prop.name)
            ? 'WORKSPACE'
            : managementRoutes.has(prop.name)
            ? 'MANAGEMENT'
            : null
          const showSection = section && section !== lastSection
          if (section) {
            lastSection = section
          }
          const isActive = location.pathname === `${prop.layout}${prop.path}`
          return (
            <React.Fragment key={`${prop.layout}${prop.path}-${key}`}>
              {showSection ? (
                <Typography className={classes.headingText} variant="h3">
                  {t(section)}
                </Typography>
              ) : null}
              <Link
                className={[
                  classes.rowDisplay,
                  classes.sidebarLink,
                  isActive && classes.active
                ]}
                href={'#' + prop.layout + prop.path}
                data-sidebar-active={isActive ? 'true' : undefined}
                onClick={() => setMobileOpen(false)}
                underline="none">
                {sidebarIcons[prop.name] ? (
                  React.createElement(sidebarIcons[prop.name], {
                    sx: {
                      fontSize: 20,
                      color: isActive ? 'common.white' : '#1F2937'
                    }
                  })
                ) : (
                  <SvgIcon
                    component={prop.icon}
                    fontSize="small"
                    sx={{
                      color: isActive ? 'common.white' : '#1F2937',
                      '& path': { fill: 'currentColor' }
                    }}
                  />
                )}

                <Typography
                  variant="h6"
                  className={[
                    classes.linkText,
                    isActive ? classes.whiteText : classes.blackText
                  ]}>
                  {t(prop.label || prop.name)}
                </Typography>
              </Link>
            </React.Fragment>
          )
        })}
      </Box>
    </Box>
  )

  const container =
    window !== undefined ? () => window().document.body : undefined

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          display: { sm: 'none' },
          bgcolor: 'common.white',
          color: 'text.primary',
          boxShadow: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="main navigation">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: '78%',
              backgroundColor: 'common.white'
            }
          }}>
          {createLinks}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: 'common.white',
              borderRight: '1px solid',
              borderColor: 'divider'
            }
          }}
          open>
          {createLinks}
        </Drawer>
      </Box>
    </>
  )
}

export default withTranslation()(AdminSidebar)
